import { Component, Input, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import MapModule from 'highcharts/modules/map';
import markerClusters from "highcharts/modules/marker-clusters";
import usMap from '@highcharts/map-collection/countries/us/us-all.topo.json';
import { Router } from '@angular/router';
import { StatusId } from '@core/models/auxiliary-company-models';
import { MessageService } from 'primeng/api';
import { CompaniesSearchService } from 'src/app/services/companies-search/companies-search.service';
import { slugify } from 'src/app/utils/slugify';
import { catchError, of, takeUntil } from 'rxjs';
import { DataFilter } from '../dynamic-table/dynamic-table.component';
import { TabStatsService } from '@core/services/tab-stats.service';
import { RouterService } from 'src/app/services/router/router.service';

// Initialize the map module
MapModule(Highcharts);
markerClusters(Highcharts);

@Component({
  selector: 'bpc-company-heat-map',
  templateUrl: './company-heat-map.component.html',
  styleUrl: './company-heat-map.component.scss'
})
export class CompanyHeatMapComponent {
  @Input() companiesFilter: DataFilter = {};

  Highcharts: typeof Highcharts = Highcharts;  // Required for the highcharts-angular wrapper
  chartConstructor = 'mapChart';  // Specifies that it's a map
  chartOptions: Highcharts.Options = {};
  showHeatmapGrid: boolean = false;  
  mapLoading: boolean = true;
  clusterData: any[] = [];
  chartData: any[] = [];
  myChart: any;
  shouldShowHeatMapGrid = false;

  constructor(
    private router: Router,
    private companiesSearchService: CompaniesSearchService,
    private messageService: MessageService,
    private tabStatsService: TabStatsService,
    private routerService: RouterService,
  ) {
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["companiesFilter"]) {
      this.generateCompanyData();
      this.showHeatmapGrid = false;
    }
  }

  onCloseGrid(value: boolean) {
    this.showHeatmapGrid = value;
  }

  loadChart() {
    const that = this;
    this.chartOptions = {
      chart: {
        map: usMap,  // Use the loaded map
        events: {
          load: function () {
            const chart = this as Highcharts.MapChart; // Ensure it's treated as a MapChart
            that.myChart = this.series[1].chart;

            // Add custom button after chart loads
            const button = chart.renderer.button('⟲ ', 10, 90, function () {
              if (chart.mapZoom) {
                chart.mapZoom();
              }
            }, {
              zIndex: 3,
              title: 'Reset Zoom',
              fill: '#ffffff',
              stroke: '#e6e6e6',
              d: '5',
              style: {
                color: '#666666',
                fontSize: '32px',
              }
            }).attr({ width: 25, height: 30 }).add();
          }
        }
      },
      title: {
        text: ''
      },

      mapNavigation: {
        enabled: true,
        buttons: {
          zoomIn: {
            x: 0, // Position the zoom-in button
            y: 0,
            width: 31,
            height: 30
          },
          zoomOut: {
            x: 0, // Position the zoom-out button
            y: 40,
            width: 31,
            height: 30
          },
        }
      },

      plotOptions: {
        mappoint: {
          tooltip: {
            clusterFormat: 'Companies: <b>{point.clusterPointsAmount}</b>',
            pointFormatter: function() {
              // Use the mapping function to convert status id to status name
              const that: any = this;
              return `<b>${this.name}</b><br>Company ID: <b>${that.id}</b><br>Status: <b>${StatusId[that.status]}</b>`;
            },
            headerFormat: '',
          },
          cluster: {
            enabled: true,
            allowOverlap: true,
            drillToCluster: true,
            marker: {
              color: '#fff',
              fillColor: '#FF6C39',  // New fill color for the cluster
            },
            animation: {
              duration: 450
            },
            layoutAlgorithm: {
              type: 'grid',
              gridSize: 50
            },
            zones: [{
              from: 1,
              to: 500,
              marker: {
                radius: 13
              }
            }, {
              from: 501,
              to: 1000,
              marker: {
                radius: 15
              }
            }, {
              from: 1001,
              to: 2000,
              marker: {
                radius: 17
              }
            }, {
              from: 2001,
              to: 3000,
              marker: {
                radius: 19
              }
            }, {
              from: 3001,
              to: 5000,
              marker: {
                radius: 21
              }
            }]
          },
          point: {
            events: {
              click: function (event: any) {
                const ev = event.point;
                if(event.point) {
                  if (event.point.isCluster) {
                    that.clusterData = [];
                    const clusteredData = event.point.clusteredData;
                    clusteredData.forEach((clusterItem: any) => {
                      if (clusterItem.options) {
                        that.clusterData.push(clusterItem.options);
                      }
                    });
                    that.showHeatmapGrid = true;
                    that.myChart = this.series.chart;
                  } else {
                    // Triggered when a point is clicked
                    if (event.point.options) {
                      const { id, name }  = event.point.options;
                      that.router.navigate(['company', id + '-' + slugify(name)]);
                    } else {
                      if(that.myChart) {
                        that.myChart.mapZoom(2, -8, 35)
                      }
                    }
                  }
                } 
              }
            }
          }
        }
      },
      series: [
        {
          name: 'US',
          type: 'map',
          accessibility: {
            exposeAsGroupOnly: true
          },
          borderColor: '#A0A0A0',
          nullColor: '#074b5c',
          showInLegend: false,
          dataLabels: {
            enabled: true,
            format: '{point.name}',  // This will display state names
            color: '#FFFFFF',  // Label text color
            style: {
              fontWeight: 'bold',
              textOutline: 'none'  // Remove text outline for better visibility
            }
          },
        },
        {
          type: 'mappoint',
          enableMouseTracking: true,
          showInLegend: false,
          accessibility: {
            point: {
              descriptionFormat: '{#if isCluster}' +
                'Grouping of {clusterPointsAmount} points.' +
                '{else}' +
                '{name}, state code: {state}.' +
                '{/if}'
            }
          },
          colorKey: 'clusterPointsAmount',
          name: 'Companies',
          data: this.chartData,
          color: '#FF6C39',
          marker: {
            lineWidth: 1,
            lineColor: '#095A70',
            symbol: 'mapmarker',
            radius: 8
          },
          dataLabels: {
            verticalAlign: 'top',
          },
          states: {
            hover: {
              enabled: false,
              opacity: 1
            }
          }
        }
      ]
    };
  }

  generateCompanyData() {
    this.mapLoading = true;
    this.shouldShowHeatMapGrid = false;

    const filterPayload = {
      //assigneeNone: this.companiesFilter.assigneeNone,
      assigneeRole: this.companiesFilter.assigneeRole,
      statusesInclude: this.companiesFilter.statusesInclude,
      assigneesInclude: this.companiesFilter.assigneesInclude,
      //rawValueMapping: this.companiesFilter.rawValueMapping,
    };
    
    this.companiesSearchService.getActiveGeoLocationCompanies(filterPayload)
    .pipe(
      takeUntil(this.routerService.destroy$),
      catchError(() => {
        this.messageService.add({ severity: 'error', summary: 'Error getting map data, try again.' });
          return of(null);
        }
      ),
    )
    .subscribe((response) => {
      if (response?.data) {
        this.chartData = response.data.data.companies;
        this.tabStatsService.update(response.data.data.companies.length);
        this.loadChart();
        this.mapLoading = false;
      }
    });
  }
}
